import { useState, useEffect } from 'react';
import api from 'services/api';

const OFFRE_COLORS = { Essentielle: '#888888', Professionnelle: '#1565c0', Premium: '#C8A84E', ESS: '#888888', PRO: '#1565c0', PRM: '#C8A84E' };

export default function MargeDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/offres/dashboard');
        setData(res.data);
      } catch {}
      setLoading(false);
    })();
  }, []);

  if (loading) return <p style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>Chargement...</p>;
  if (!data) return <p style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>Erreur de chargement</p>;

  const maxRevenu = Math.max(...data.offres.map(o => o.revenu_mensuel), 1);

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1A1A2E', margin: '0 0 20px' }}>Marge & Rentabilite</h2>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Revenu mensuel total</span></div>
          <div className="stat-card-value" style={{ color: '#1B3A5C' }}>{data.total_revenu.toLocaleString()} F</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Marge moyenne</span></div>
          <div className="stat-card-value" style={{ color: '#2e7d32' }}>{data.marge_moyenne}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Clients Premium (PRM)</span></div>
          <div className="stat-card-value" style={{ color: '#C8A84E' }}>{data.clients_premium}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Clients a migrer</span></div>
          <div className="stat-card-value" style={{ color: '#DC2626' }}>{data.upsell.length}</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
        {/* Revenue by offre */}
        <div className="content-card" style={{ padding: 20 }}>
          <div className="content-card-title" style={{ marginBottom: 16 }}>Revenu mensuel par offre</div>
          {data.offres.map(o => (
            <div key={o.id} style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                <span style={{ fontWeight: 700, color: OFFRE_COLORS[o.nom] || '#666' }}>{o.nom}</span>
                <span style={{ fontWeight: 600 }}>{o.revenu_mensuel.toLocaleString()} FCFA</span>
              </div>
              <div style={{ height: 24, background: '#F0F0F0', borderRadius: 6, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 6,
                  width: `${Math.max(2, (o.revenu_mensuel / maxRevenu) * 100)}%`,
                  background: OFFRE_COLORS[o.nom] || '#888',
                  display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 8,
                }}>
                  {o.revenu_mensuel > 0 && <span style={{ fontSize: 10, color: '#FFF', fontWeight: 700 }}>{o.nb_contrats} contrat{o.nb_contrats > 1 ? 's' : ''}</span>}
                </div>
              </div>
            </div>
          ))}
          {data.offres.every(o => o.revenu_mensuel === 0) && (
            <p style={{ color: '#999', fontSize: 13, textAlign: 'center', padding: 20 }}>Aucun contrat avec offre rattachee</p>
          )}
        </div>

        {/* Margin table */}
        <div className="content-card" style={{ padding: 20 }}>
          <div className="content-card-title" style={{ marginBottom: 16 }}>Rentabilite par offre</div>
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead>
                <tr>
                  <th>Offre</th>
                  <th>Tarif/agent</th>
                  <th>Cout agent</th>
                  <th>Marge</th>
                  <th>Rentabilite</th>
                </tr>
              </thead>
              <tbody>
                {data.offres.map(o => {
                  const margeAgent = o.tarif_agent - o.cout_agent;
                  const rentAgent = o.tarif_agent > 0 ? ((margeAgent / o.tarif_agent) * 100).toFixed(1) : '0.0';
                  return (
                    <tr key={o.id}>
                      <td><strong style={{ color: OFFRE_COLORS[o.nom] || '#666' }}>{o.nom}</strong></td>
                      <td>{o.tarif_agent.toLocaleString()} F</td>
                      <td>{o.cout_agent.toLocaleString()} F</td>
                      <td style={{ fontWeight: 700, color: margeAgent > 0 ? '#2e7d32' : '#DC2626' }}>{margeAgent.toLocaleString()} F</td>
                      <td>
                        <span style={{
                          padding: '2px 8px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                          background: Number(rentAgent) >= 40 ? '#E8F5E9' : Number(rentAgent) >= 20 ? '#FFF3E0' : '#FEE2E2',
                          color: Number(rentAgent) >= 40 ? '#2e7d32' : Number(rentAgent) >= 20 ? '#E65100' : '#DC2626',
                        }}>{rentAgent}%</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 12, padding: 10, background: '#F5F5F5', borderRadius: 8, fontSize: 12, color: '#666' }}>
            Cout agent = SMIG ({data.cout_agent.toLocaleString()} F) + CNSS 17.5% + transport + prime risque + panier nuit
          </div>
        </div>
      </div>

      {/* Upsell list */}
      {data.upsell.length > 0 && (
        <div className="content-card" style={{ padding: 20, marginTop: 16 }}>
          <div className="content-card-title" style={{ marginBottom: 16 }}>Opportunites de montee en gamme</div>
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead>
                <tr><th>Client</th><th>Offre actuelle</th><th>Agents</th><th>Montant/mois</th><th>Action</th></tr>
              </thead>
              <tbody>
                {data.upsell.map((u, i) => (
                  <tr key={i}>
                    <td><strong>{u.raison_sociale}</strong></td>
                    <td><span style={{
                      padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                      background: u.offre_code === 'ESS' ? '#F0F0F0' : '#EFF6FF',
                      color: OFFRE_COLORS[u.offre_code] || '#666',
                    }}>{u.offre_actuelle}</span></td>
                    <td>{u.nb_agents}</td>
                    <td style={{ fontWeight: 600 }}>{Number(u.montant_mensuel || 0).toLocaleString()} F</td>
                    <td>
                      <span style={{ fontSize: 11, color: '#C8A84E', fontWeight: 700 }}>
                        Proposer {u.offre_code === 'ESS' ? 'PRO ou PRM' : 'PRM'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
